/**
 * Parses a search query string into first and last name components.
 * Handles multi-part names by treating everything after the first word as the last name.
 *
 * @param query - The search query to parse
 * @returns Object containing firstName and lastName
 *
 * @example
 * parseNameFromQuery("John Doe") // { firstName: "John", lastName: "Doe" }
 * parseNameFromQuery("John Van Der Berg") // { firstName: "John", lastName: "Van Der Berg" }
 * parseNameFromQuery("Madonna") // { firstName: "Madonna", lastName: "" }
 */
export function parseNameFromQuery(query: string): {
  firstName: string;
  lastName: string;
} {
  // Sanitize input by trimming and removing excessive whitespace
  const sanitizedQuery = query.trim().replace(/\s+/g, ' ');

  if (!sanitizedQuery) {
    return { firstName: '', lastName: '' };
  }

  const nameParts = sanitizedQuery.split(' ');

  return {
    firstName: nameParts[0] || '',
    lastName: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
  };
}

/**
 * Sanitizes user input to prevent XSS attacks.
 * Removes any HTML tags and dangerous characters.
 *
 * @param input - The user input to sanitize
 * @returns Sanitized string safe for use in forms
 */
export function sanitizeUserInput(input: string): string {
  // Remove any HTML tags
  const withoutTags = input.replace(/<[^>]*>/g, '');

  // Remove dangerous characters while preserving valid name characters
  // Allow letters, numbers, spaces, hyphens, apostrophes, and periods
  const sanitized = withoutTags.replace(/[^a-zA-Z0-9\s\-'.]/g, '');

  // Normalize whitespace
  return sanitized.trim().replace(/\s+/g, ' ');
}
